import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Wallet, Layers, Plus, Trash2, Save, Info, AlertTriangle, Bell, Users, ArrowRight } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { PageHeader, SectionCard, Spinner } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtMoney, currencySymbol } from "@/lib/constants";
import { toast } from "sonner";

const CURRENCIES = ["EUR", "USD", "GBP"];

export default function RatesBudget() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currency, setCurrency] = useState("EUR");
  const [budgetTotal, setBudgetTotal] = useState("");
  const [budgetUsed, setBudgetUsed] = useState(0);
  const [alertThreshold, setAlertThreshold] = useState(80);
  const [allocatedTotal, setAllocatedTotal] = useState(0);
  const [bands, setBands] = useState([]);

  const load = () => {
    setLoading(true);
    api.get("/company/rate-settings").then(({ data }) => {
      setCurrency(data.currency || "EUR");
      setBudgetTotal(String(data.budget_total || 0));
      setBudgetUsed(data.budget_used || 0);
      setAlertThreshold(data.alert_threshold_pct ?? 80);
      setAllocatedTotal(data.allocated_total || 0);
      setBands(data.bands || []);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const updateBand = (i, field, value) => setBands((b) => b.map((x, idx) => (idx === i ? { ...x, [field]: value } : x)));
  const addBand = () => setBands((b) => [...b, { band_id: null, label: "", min_years: 0, day_rate: 0 }]);
  const removeBand = (i) => {
    if (bands.length <= 1) { toast.error(t("rates.errAtLeastOne")); return; }
    setBands((b) => b.filter((_, idx) => idx !== i));
  };

  const save = async () => {
    if (bands.some((b) => !String(b.label || "").trim())) { toast.error(t("rates.errLabels")); return; }
    setSaving(true);
    try {
      await api.put("/company/rate-settings", {
        currency,
        budget_total: Number(budgetTotal) || 0,
        alert_threshold_pct: Number(alertThreshold) || 0,
        bands: bands.map((b) => ({
          band_id: b.band_id || undefined,
          label: String(b.label).trim(),
          min_years: Number(b.min_years) || 0,
          day_rate: Number(b.day_rate) || 0,
        })),
      });
      toast.success(t("rates.toastSaved"));
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const sym = currencySymbol(currency);
  const total = Number(budgetTotal) || 0;
  const remaining = Math.max(0, total - budgetUsed);
  const usedPct = total > 0 ? Math.round((budgetUsed / total) * 100) : 0;
  const overThreshold = total > 0 && usedPct >= (Number(alertThreshold) || 0);
  const overAllocated = allocatedTotal > total;
  const topBand = [...bands].sort((a, b) => (b.day_rate || 0) - (a.day_rate || 0))[0];
  const sampleCost = topBand ? Math.round(((topBand.day_rate || 0) * 2) / 8) : 0;

  if (loading) return <AppShell><div className="grid place-items-center py-32"><Spinner className="text-primary w-7 h-7" /></div></AppShell>;

  return (
    <AppShell>
      <PageHeader eyebrow={t("hr.eyebrow")} title={t("rates.title")} description={t("rates.subtitle")}>
        <Button data-testid="save-rates" onClick={save} disabled={saving} className="rounded-lg gap-1.5 active:scale-[0.98] transition-transform">
          {saving ? <Spinner /> : <><Save size={15} /> {t("rates.save")}</>}
        </Button>
      </PageHeader>

      {overThreshold && (
        <div data-testid="budget-alert" className="mb-6 flex items-start gap-3 rounded-[0.7rem] border border-warning/40 bg-warning/5 p-4 animate-fade-up">
          <AlertTriangle size={18} className="text-warning shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-foreground">{t("rates.alertBannerTitle")}</p>
            <p className="text-sm text-muted-foreground">{t("rates.alertBannerBody", { pct: usedPct, threshold: Number(alertThreshold) || 0 })}</p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        {/* Budget */}
        <SectionCard icon={Wallet} title={t("rates.budgetTitle")} description={t("rates.budgetDesc")} className="animate-fade-up d1">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm">{t("rates.totalLabel")}</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{sym}</span>
                <Input data-testid="budget-total" type="number" min="0" value={budgetTotal} onChange={(e) => setBudgetTotal(e.target.value)} className="h-11 rounded-lg pl-7 tabular" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">{t("rates.currencyLabel")}</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="currency-select" className="h-11 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c} ({currencySymbol(c)})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5 mt-4">
            <Label className="text-sm flex items-center gap-1.5"><Bell size={13} className="text-primary" /> {t("rates.alertThresholdLabel")}</Label>
            <div className="relative max-w-[10rem]">
              <Input data-testid="alert-threshold" type="number" min="0" max="100" value={alertThreshold} onChange={(e) => setAlertThreshold(e.target.value)} className="h-11 rounded-lg pr-8 tabular" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
            </div>
            <p className="text-xs text-muted-foreground">{t("rates.alertThresholdHint")}</p>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="rounded-lg border border-border p-3">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{t("rates.used")}</p>
              <p className="text-lg font-semibold tabular mt-0.5">{fmtMoney(budgetUsed, currency)}</p>
              {total > 0 && <p className="text-[11px] text-muted-foreground mt-0.5">{usedPct}% {t("rates.ofBudget")}</p>}
            </div>
            <div className="rounded-lg border border-border p-3">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{t("rates.remaining")}</p>
              <p className="text-lg font-semibold tabular mt-0.5 text-success">{fmtMoney(remaining, currency)}</p>
            </div>
          </div>
        </SectionCard>

        {/* Allocation summary */}
        <SectionCard icon={Users} title={t("rates.allocationTitle")} description={t("rates.allocationDesc")} className="animate-fade-up d2">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-border p-3">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{t("rates.allocatedLabel")}</p>
              <p className="text-lg font-semibold tabular mt-0.5">{fmtMoney(allocatedTotal, currency)}</p>
            </div>
            <div className="rounded-lg border border-border p-3">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{t("rates.unallocatedLabel")}</p>
              <p className={`text-lg font-semibold tabular mt-0.5 ${overAllocated ? "text-destructive" : ""}`}>{fmtMoney(total - allocatedTotal, currency)}</p>
            </div>
          </div>
          {overAllocated && (
            <p className="text-xs text-destructive flex items-center gap-1.5 mt-3"><AlertTriangle size={13} className="shrink-0" /> {t("rates.overAllocated")}</p>
          )}
          <Link to="/hr/employees" data-testid="manage-allocations">
            <Button variant="outline" className="rounded-lg gap-1.5 mt-4 bg-card w-full sm:w-auto">
              <Users size={15} /> {t("rates.manageAllocations")} <ArrowRight size={14} />
            </Button>
          </Link>
        </SectionCard>

        {/* Example / info */}
        <SectionCard icon={Info} title={t("rates.sampleTitle")} description="" className="animate-fade-up d2">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {topBand ? t("rates.sampleBody", {
              band: topBand.label, cost: fmtMoney(sampleCost, currency), rate: fmtMoney(topBand.day_rate, currency),
            }) : t("rates.bandsDesc")}
          </p>
        </SectionCard>

        {/* Bands */}
        <SectionCard icon={Layers} title={t("rates.bandsTitle")} description={t("rates.bandsDesc")} className="lg:col-span-2 animate-fade-up d3">
          <div className="space-y-3">
            <div className="hidden sm:grid grid-cols-[1.4fr_1fr_1.2fr_auto] gap-3 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <span>{t("rates.colBand")}</span>
              <span>{t("rates.colMinYears")}</span>
              <span>{t("rates.colDayRate")}</span>
              <span className="w-8" />
            </div>
            {bands.map((b, i) => (
              <div key={b.band_id || `new-${i}`} data-testid={`band-row-${i}`} className="grid grid-cols-1 sm:grid-cols-[1.4fr_1fr_1.2fr_auto] gap-3 items-center rounded-lg border border-border p-3">
                <Input data-testid={`band-label-${i}`} value={b.label} onChange={(e) => updateBand(i, "label", e.target.value)} placeholder={t("rates.bandLabelPlaceholder")} className="h-10 rounded-lg" />
                <Input data-testid={`band-years-${i}`} type="number" min="0" value={b.min_years} onChange={(e) => updateBand(i, "min_years", e.target.value)} className="h-10 rounded-lg tabular" />
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{sym}</span>
                  <Input data-testid={`band-rate-${i}`} type="number" min="0" value={b.day_rate} onChange={(e) => updateBand(i, "day_rate", e.target.value)} className="h-10 rounded-lg pl-7 tabular" />
                </div>
                <button data-testid={`band-remove-${i}`} onClick={() => removeBand(i)} title={t("rates.removeBand")}
                  className="grid place-items-center w-9 h-9 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-colors justify-self-end">
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
          <Button data-testid="add-band" variant="outline" onClick={addBand} className="rounded-lg gap-1.5 mt-4 bg-card">
            <Plus size={15} /> {t("rates.addBand")}
          </Button>
        </SectionCard>
      </div>
    </AppShell>
  );
}
